// Live NPC rendering for Tilemap2DScene: an externally-positioned,
// externally-animated character alongside the single player and the static
// WorldEntities list. Position, sprite and animation are entirely
// consumer-driven per frame (a game's own schedule/AI/server logic decides
// where an NPC is and what it's doing); this module only owns the pure,
// framework-agnostic bits of turning that into paintable frames, matching
// topDown.ts/sprite2d.ts's own split from the .tsx that renders them.
//
// Deliberately not part of the persisted Tilemap2D schema
// (content/schema/map.ts): unlike Tilemap2DEntity, a Tilemap2DNpc is never
// baked into a map document, so it carries a sprite by value rather than a
// spriteId looked up against map.sprites, the same way Player already
// takes map.playerSprite directly rather than a lookup.
import type { Sprite2D, Tilemap2DEntity, WaypointPatrol } from '../../content/schema/map';
import { patrolSampleAt } from '../waypointPatrol';

export type Tilemap2DNpcFacing = 'left' | 'right';

export interface Tilemap2DNpc {
  id: string;
  x: number;
  y: number;
  sprite: Sprite2D;
  /** Clip name into sprite.animations, or any string if the sprite has none
   * (spriteAnimationCells already falls back to the flat flipbook). An open
   * string, not a closed union, matching Tilemap2DScene's existing
   * top-down pose vocabulary (Player's own animStateRef is a plain string
   * too, not Platformer2DScene's PoseId union). */
  animation: string;
  /** Omitted draws the sprite unflipped, as authored. */
  facing?: Tilemap2DNpcFacing;
  /**
   * Optional authored patrol route (see {@link WaypointPatrol}). When set,
   * `Tilemap2DScene` walks this NPC between its waypoints itself - `x`/`y`/
   * `animation`/`facing` above are then only the frame-zero starting values,
   * not per-frame consumer input - so a game no longer needs bespoke
   * per-frame code just to get a moving NPC. Omitted keeps a `Tilemap2DNpc`
   * exactly as consumer-driven as before this field existed.
   */
  patrol?: WaypointPatrol;
}

export interface AnimationClock {
  name: string;
  elapsed: number;
}

/** Advances one frame: elapsed accumulates while `animation` is unchanged,
 * resets to 0 the instant it changes. The same transition Player already
 * does inline via animStateRef/animElapsedRef, pulled out once an
 * arbitrary list of NPCs needed it too rather than one hardcoded ref pair. */
export function stepAnimationClock(prev: AnimationClock, animation: string, dt: number): AnimationClock {
  if (prev.name !== animation) return { name: animation, elapsed: 0 };
  return { name: animation, elapsed: prev.elapsed + dt };
}

/** The animation clip names a patrol-driven `Tilemap2DNpc` selects on its
 * own: 'idle' while dwelling at a waypoint, 'walk' while travelling between
 * two - the same names `Player`'s own top-down flipbook already uses, so an
 * existing NPC sprite authored with those two clips needs no new art to gain
 * a patrol. `spriteAnimationCells` already falls back to the flat flipbook
 * for a sprite with no clip of that name, so a patrol still renders on a
 * sprite authored with neither. */
export const PATROL_IDLE_ANIMATION = 'idle';
export const PATROL_WALK_ANIMATION = 'walk';

/** A patrol-driven `Tilemap2DNpc`'s position/animation/facing at
 * `elapsedMs` since its patrol started - the per-frame result
 * `Tilemap2DScene`'s own `Npc` component applies in place of the consumer-
 * supplied `x`/`y`/`animation`/`facing` whenever `npc.patrol` is set. Pure,
 * like `patrolSampleAt` itself: the same elapsed time always resolves to the
 * same frame. */
export interface Tilemap2DNpcPatrolFrame {
  x: number;
  y: number;
  animation: string;
  facing?: Tilemap2DNpcFacing;
}

export function tilemap2DNpcPatrolFrame(patrol: WaypointPatrol, elapsedMs: number): Tilemap2DNpcPatrolFrame {
  const sample = patrolSampleAt(patrol, elapsedMs);
  const facing: Tilemap2DNpcFacing | undefined = sample.direction.x > 0 ? 'right' : sample.direction.x < 0 ? 'left' : undefined;
  return {
    x: sample.position.x,
    y: sample.position.y,
    animation: sample.dwelling ? PATROL_IDLE_ANIMATION : PATROL_WALK_ANIMATION,
    facing,
  };
}

/** True when a sprite has no painted pixels on any cell. Centralises the
 * check Player and WorldEntity each already inline separately in
 * Tilemap2DScene.tsx. */
export function isSpriteEmpty(sprite: Sprite2D): boolean {
  return !sprite.cells.some((cell) => cell !== null);
}

/** Horizontal mesh scale for a facing-flippable side-profile sprite: -1
 * mirrors a right-facing rig to face left. 'right' or omitted draws
 * unflipped, matching Furrow's own TopDownCatPlayer.tsx convention. */
export function facingScaleX(facing?: Tilemap2DNpcFacing): 1 | -1 {
  return facing === 'left' ? -1 : 1;
}

/** A single tile: the footprint every `Tilemap2DEntity` placed before
 * `footprint` existed implicitly has. */
const DEFAULT_ENTITY_FOOTPRINT = { w: 1, h: 1 };

/** World-space plane dimensions for a placed entity's footprint, in
 * `tileSize` units. Reduces to the fixed 0.8-tile-square quad every
 * entity has always rendered at when `footprint` is absent, so an entity
 * placed before the field existed is pixel-identical to before; a wider
 * or taller footprint scales the same 0.8 margin proportionally, keeping
 * one consistent sizing rule rather than a special case per footprint
 * size. Centralises the maths `WorldEntity` would otherwise inline, the
 * same split this module already draws for `isSpriteEmpty`/`facingScaleX`. */
export function entityFootprintSize(footprint: Tilemap2DEntity['footprint'], tileSize: number): { width: number; height: number } {
  const { w, h } = footprint ?? DEFAULT_ENTITY_FOOTPRINT;
  return { width: w * tileSize * 0.8, height: h * tileSize * 0.8 };
}

/** World-space centre of a placed entity anchored at grid cell (`x`, `y`)
 * as its footprint's top-left corner. Reduces to the existing single-cell
 * centre, `(x + 0.5, y + 0.5)`, when `footprint` is absent. */
export function entityFootprintCenter(x: number, y: number, footprint: Tilemap2DEntity['footprint'], tileSize: number): { x: number; y: number } {
  const { w, h } = footprint ?? DEFAULT_ENTITY_FOOTPRINT;
  return { x: (x + w / 2) * tileSize, y: (y + h / 2) * tileSize };
}
